import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Clock,
  Link2,
  CreditCard,
  MessageCircle,
  Pencil,
  Trash2,
  FileSpreadsheet,
  FileDown,
  Sparkles,
  User,
  Phone,
  Mail,
  Calendar,
  ReceiptText,
  Loader2,
  SearchX,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { getInvoiceByIdAPI, shareInvoiceWhatsAppAPI, deleteInvoiceAPI } from "../../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import { DocumentPrintTemplate } from "../DocumentPrintTemplate";
import PaymentRecordModal from "./components/PaymentRecordModal";
import PaymentLinkModal from "./components/PaymentLinkModal";
import InvoiceShareModal from "./components/InvoiceShareModal";
import { downloadElementAsPDF, exportElementAsPDF } from "../../../../Utils/pdfHelpers";
import type { Invoice, InvoiceInstallment } from "../../../../Data/invoice.dto";
import { INVOICE_STATUS_LABELS, PAYMENT_STATUS_LABELS, PAYMENT_METHOD_LABELS } from "../../../../Data/invoice.dto";
import { useAppSelector } from "../../../../Redux/Hooks";
import { formatINR } from "../../../../Utils/helper";
import { device } from "../../../atoms/Utlis/media";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "../Marketplace/MarketplaceEmptyState";
import {
  TableWrap,
  PaymentsTable,
  AmountCell,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../../hooks/usePermissions";

const InvoiceDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, studio_logo, business } = useAppSelector((state) => state.auth);
  const queryParams = new URLSearchParams(location.search);
  const invoiceId = queryParams.get("id");
  const initialTab = queryParams.get("tab") || "details";

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"details" | "payments">(initialTab as "details" | "payments");

  const printRef = useRef<HTMLDivElement>(null);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [paymentRecordInstallment, setPaymentRecordInstallment] = useState<InvoiceInstallment | null>(null);
  const [paymentLinkInstallment, setPaymentLinkInstallment] = useState<InvoiceInstallment | null>(null);

  const fetchDetails = async () => {
    if (!invoiceId) {
      setLoading(false);
      return;
    }
    try {
      const res = await getInvoiceByIdAPI(invoiceId);
      setInvoice(res);
    } catch (err) {
      showErrorToast("Failed to fetch invoice details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [invoiceId]);

  const handleDownloadPDF = async () => {
    if (!printRef.current || !invoice) return;
    try {
      await downloadElementAsPDF(printRef.current, `Invoice_${invoice.invoice_no}.pdf`);
      showSuccessToast("PDF downloaded successfully");
    } catch {
      showErrorToast("Failed to generate PDF");
    }
  };

  const [sharingOfficial, setSharingOfficial] = useState(false);

  const handleOfficialShare = async () => {
    if (!printRef.current || !invoice) return;
    try {
      setSharingOfficial(true);
      await new Promise((r) => setTimeout(r, 300));
      const blob = await exportElementAsPDF(printRef.current);
      const filename = `Invoice_${invoice.invoice_no}.pdf`;
      const result = await shareInvoiceWhatsAppAPI(invoice.id, blob, filename);
      showSuccessToast(`Invoice sent to ${result.sent_to || invoice.customer_phone} on WhatsApp 🎉`);
      fetchDetails();
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to share invoice on WhatsApp.");
    } finally {
      setSharingOfficial(false);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!invoice) return;
    setDeleting(true);
    try {
      await deleteInvoiceAPI(invoice.id);
      showSuccessToast("Invoice deleted successfully");
      navigate("/dashboard/invoice");
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to delete invoice");
    } finally {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleExportCSV = () => {
    if (!invoice) return;
    let csv = `Invoice Details\n`;
    csv += `Invoice No,${invoice.invoice_no}\n`;
    csv += `Invoice Date,${invoice.invoice_date}\n`;
    csv += `Customer Name,${invoice.customer_name}\n`;
    csv += `Customer Phone,${invoice.customer_phone}\n`;
    csv += `Customer Email,${invoice.customer_email || ""}\n\n`;

    if (invoice.line_items && invoice.line_items.length > 0) {
      csv += `LINE ITEMS\n`;
      csv += `#,Description,Quantity,Unit Price,Total\n`;
      invoice.line_items.forEach((item, i) => {
        csv += `"${i + 1}","${item.description || ""}","${item.quantity}","${item.unit_price}","${(item.quantity * parseFloat(item.unit_price)).toFixed(2)}"\n`;
      });
      csv += `\n`;
    }

    csv += `PRICING\n`;
    csv += `Subtotal,₹${formatINR(invoice.subtotal)}\n`;
    csv += `Discount %,${invoice.discount_percent || 0}\n`;
    csv += `Tax %,${invoice.tax_percent || 0}\n`;
    csv += `Grand Total,₹${formatINR(invoice.grand_total)}\n`;
    csv += `Amount Paid,₹${formatINR(invoice.amount_paid)}\n`;
    csv += `Balance Due,₹${formatINR(invoice.remaining_amount ?? invoice.balance_due)}\n`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoice_${invoice.invoice_no}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessToast("CSV exported successfully");
  };

  const handleExportExcel = () => {
    if (!invoice) return;
    let xls = `Invoice Details\t${invoice.invoice_no}\n`;
    xls += `Invoice Date\t${invoice.invoice_date}\n`;
    xls += `Customer Name\t${invoice.customer_name}\n`;
    xls += `Customer Phone\t${invoice.customer_phone}\n`;
    xls += `Customer Email\t${invoice.customer_email || ""}\n\n`;

    if (invoice.line_items && invoice.line_items.length > 0) {
      xls += `LINE ITEMS\n`;
      xls += `#\tDescription\tQuantity\tUnit Price\tTotal\n`;
      invoice.line_items.forEach((item, i) => {
        xls += `${i + 1}\t${item.description || ""}\t${item.quantity}\t${item.unit_price}\t${(item.quantity * parseFloat(item.unit_price)).toFixed(2)}\n`;
      });
      xls += `\n`;
    }

    xls += `PRICING\n`;
    xls += `Subtotal\t₹${formatINR(invoice.subtotal)}\n`;
    xls += `Discount %\t${invoice.discount_percent || 0}\n`;
    xls += `Tax %\t${invoice.tax_percent || 0}\n`;
    xls += `Grand Total\t₹${formatINR(invoice.grand_total)}\n`;
    xls += `Amount Paid\t₹${formatINR(invoice.amount_paid)}\n`;
    xls += `Balance Due\t₹${formatINR(invoice.remaining_amount ?? invoice.balance_due)}\n`;

    const blob = new Blob([xls], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoice_${invoice.invoice_no}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessToast("Excel exported successfully");
  };

  if (loading) {
    return (
      <PageContainer>
        <MarketplaceLoadingSpinner padding="80px 0" />
      </PageContainer>
    );
  }

  if (!invoice) {
    return (
      <PageContainer>
        <Hero>
          <HeroGlow />
          <HeroContent>
            <HeroLeft>
              <BackBtn type="button" onClick={() => navigate(-1)} aria-label="Go back">
                <ArrowLeft size={18} />
              </BackBtn>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={13} />
                  Invoice
                </HeroBadge>
                <PageTitle className="linear-text">Invoice Details</PageTitle>
              </HeroText>
            </HeroLeft>
          </HeroContent>
        </Hero>
        <MarketplaceEmptyState
          icon={<SearchX size={36} strokeWidth={1.5} />}
          title="Invoice not found"
          description="This invoice may have been deleted or the link is invalid."
          actions={[
            {
              label: "Back to invoices",
              onClick: () => navigate("/dashboard/invoice"),
            },
          ]}
        />
      </PageContainer>
    );
  }

  const balanceDue = Number(invoice.remaining_amount ?? invoice.balance_due) || 0;
  const sharingStatus = INVOICE_STATUS_LABELS[invoice.invoice_status] || invoice.invoice_status;
  const paymentStatus = PAYMENT_STATUS_LABELS[invoice.payment_status] || invoice.payment_status;

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackBtn type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackBtn>
            <HeroText>
              <HeroBadge>
                <Sparkles size={13} />
                Invoice {invoice.invoice_no}
              </HeroBadge>
              <TitleRow>
                <PageTitle className="linear-text">Invoice Details</PageTitle>
                <StatusBadge $status={paymentStatus}>{paymentStatus}</StatusBadge>
              </TitleRow>
              <PageSubtitle>
                Manage details, view payments, and share with customer.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>
        </HeroContent>
      </Hero>

      <StatsGrid>
        <StatCard $tone="#c94daa">
          <StatLabel>Grand Total</StatLabel>
          <StatValue>₹{formatINR(invoice.grand_total)}</StatValue>
        </StatCard>
        <StatCard $tone="#31a66a">
          <StatLabel>Amount Paid</StatLabel>
          <StatValue>₹{formatINR(invoice.amount_paid)}</StatValue>
        </StatCard>
        <StatCard $tone="#e44862">
          <StatLabel>Balance Due</StatLabel>
          <StatValue>₹{formatINR(balanceDue)}</StatValue>
        </StatCard>
        <StatCard $tone="#f09a3e">
          <StatLabel>Sharing Status</StatLabel>
          <StatValue style={{ fontSize: 16 }}>
            <InlineBadge $status={sharingStatus}>{sharingStatus}</InlineBadge>
          </StatValue>
        </StatCard>
      </StatsGrid>

      <TabRow>
        <TabPill $active={activeTab === "details"} type="button" onClick={() => setActiveTab("details")}>
          Invoice Details
        </TabPill>
        <TabPill $active={activeTab === "payments"} type="button" onClick={() => setActiveTab("payments")}>
          Payments & Installments
        </TabPill>
      </TabRow>

      <ScrollBody>
        <SectionCard>
          <ActionRow>
            <ActionGroup>
              <SoftBtn $tone="#35b7c8" type="button" onClick={handleExportCSV}>
                <FileText size={15} strokeWidth={2.2} />
                CSV
              </SoftBtn>
              <SoftBtn $tone="#31a66a" type="button" onClick={handleExportExcel}>
                <FileSpreadsheet size={15} strokeWidth={2.2} />
                Excel
              </SoftBtn>
              <SoftBtn $tone="#e44862" type="button" onClick={handleDownloadPDF}>
                <FileDown size={15} strokeWidth={2.2} />
                PDF
              </SoftBtn>
            </ActionGroup>

            <ActionDivider />

            <ActionGroup>
              <SoftBtn
                $tone="#25d366"
                type="button"
                disabled={sharingOfficial}
                onClick={() => setShareModalOpen(true)}
              >
                {sharingOfficial ? <SpinIcon size={15} /> : <MessageCircle size={15} strokeWidth={2.2} />}
                {sharingOfficial ? "Sending..." : "Share"}
              </SoftBtn>
            </ActionGroup>

            <ActionSpacer />

            <ActionGroup>
              <PermissionGate anyPermission={["invoice.update", "payments_management.update"]}>
                <SoftBtn
                  $tone="#c94daa"
                  type="button"
                  onClick={() => navigate(`/dashboard/invoice/edit?id=${invoice.id}`)}
                >
                  <Pencil size={15} strokeWidth={2.2} />
                  Edit
                </SoftBtn>
              </PermissionGate>
              <PermissionGate anyPermission={["invoice.create", "invoice.update", "payments_management.create", "payments_management.update"]}>
                <SoftBtn
                  $tone="#6366f1"
                  type="button"
                  onClick={() => {
                    setActiveTab("payments");
                    setPaymentRecordInstallment({} as InvoiceInstallment);
                  }}
                >
                  <CreditCard size={15} strokeWidth={2.2} />
                  Record Payment
                </SoftBtn>
              </PermissionGate>
              <PermissionGate anyPermission={["invoice.delete", "payments_management.delete"]}>
                <SoftBtn $tone="#e44862" $solid type="button" onClick={() => setDeleteConfirm(true)}>
                  <Trash2 size={15} strokeWidth={2.2} />
                  Delete
                </SoftBtn>
              </PermissionGate>
            </ActionGroup>
          </ActionRow>
        </SectionCard>

        {activeTab === "details" && (
          <>
            <SectionCard>
              <CardHeader>
                <CardIcon $tone="#c94daa">
                  <User size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Customer & Info</CardTitle>
                  <CardSubtitle>Contact details and invoice metadata</CardSubtitle>
                </div>
              </CardHeader>

              <InfoGrid>
                <InfoItem>
                  <InfoLabel><User size={12} /> Customer Name</InfoLabel>
                  <InfoValue>{invoice.customer_name}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><Phone size={12} /> Phone Number</InfoLabel>
                  <InfoValue>{invoice.customer_phone}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><Mail size={12} /> Email</InfoLabel>
                  <InfoValue>{invoice.customer_email || "—"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><Calendar size={12} /> Invoice Date</InfoLabel>
                  <InfoValue>{invoice.invoice_date}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><Calendar size={12} /> Due Date</InfoLabel>
                  <InfoValue>{invoice.due_date || "—"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel><ReceiptText size={12} /> Sharing Status</InfoLabel>
                  <InfoValue>
                    <InlineBadge $status={sharingStatus}>{sharingStatus}</InlineBadge>
                  </InfoValue>
                </InfoItem>
              </InfoGrid>
            </SectionCard>

            <SectionCard>
              <CardHeader>
                <CardIcon $tone="#35b7c8">
                  <FileText size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Line Items</CardTitle>
                  <CardSubtitle>Products and services on this invoice</CardSubtitle>
                </div>
              </CardHeader>

              <TableWrap>
                <PaymentsTable $minWidth={640}>
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}><span>#</span></th>
                      <th><span>Description</span></th>
                      <th style={{ textAlign: "center" }}><span>Qty</span></th>
                      {!invoice.without_price && <th style={{ textAlign: "right" }}><span>Unit Price</span></th>}
                      {!invoice.without_price && <th style={{ textAlign: "right" }}><span>Total</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items.map((item, i) => (
                      <tr key={item.id || i}>
                        <td style={{ color: "rgba(23,23,23,0.45)", fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{item.description}</td>
                        <td style={{ textAlign: "center" }}>{item.quantity}</td>
                        {!invoice.without_price && (
                          <td style={{ textAlign: "right" }}>₹{formatINR(item.unit_price)}</td>
                        )}
                        {!invoice.without_price && (
                          <td style={{ textAlign: "right" }}>
                            <AmountCell>₹{formatINR(item.quantity * parseFloat(item.unit_price))}</AmountCell>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </PaymentsTable>
              </TableWrap>

              {!invoice.without_price && (
                <SummaryCard>
                  <SummaryRow><span>Subtotal</span><span>₹{formatINR(invoice.subtotal)}</span></SummaryRow>
                  <SummaryRow>
                    <span>Discount ({invoice.discount_percent}%)</span>
                    <span style={{ color: "#e44862" }}>-₹{formatINR(invoice.discount_amount)}</span>
                  </SummaryRow>
                  <SummaryRow>
                    <span>Tax ({invoice.tax_percent}%)</span>
                    <span>₹{formatINR(invoice.tax_amount)}</span>
                  </SummaryRow>
                  <SummaryTotal>
                    <span>Grand Total</span>
                    <span>₹{formatINR(invoice.grand_total)}</span>
                  </SummaryTotal>
                </SummaryCard>
              )}
            </SectionCard>

            {invoice.notes && (
              <SectionCard>
                <CardHeader>
                  <CardIcon $tone="#6366f1">
                    <FileText size={18} />
                  </CardIcon>
                  <div>
                    <CardTitle>Notes</CardTitle>
                    <CardSubtitle>Additional remarks</CardSubtitle>
                  </div>
                </CardHeader>
                <NotesBlock>{invoice.notes}</NotesBlock>
              </SectionCard>
            )}

            {invoice.add_terms_conditions !== false && invoice.terms_and_conditions && (
              <SectionCard>
                <CardHeader>
                  <CardIcon $tone="#c94daa">
                    <FileText size={18} />
                  </CardIcon>
                  <div>
                    <CardTitle>Terms & Conditions</CardTitle>
                    <CardSubtitle>Policy terms for this invoice</CardSubtitle>
                  </div>
                </CardHeader>
                <NotesBlock style={{ whiteSpace: "pre-wrap" }}>{invoice.terms_and_conditions}</NotesBlock>
              </SectionCard>
            )}
          </>
        )}

        {activeTab === "payments" && (
          <>
            <SectionCard>
              <CardHeader>
                <CardIcon $tone="#31a66a">
                  <CreditCard size={18} />
                </CardIcon>
                <div style={{ flex: 1 }}>
                  <CardTitle>Installment Schedule</CardTitle>
                  <CardSubtitle>Payment milestones and collection actions</CardSubtitle>
                </div>
                <PermissionGate anyPermission={["invoice.create", "invoice.update", "payments_management.create", "payments_management.update"]}>
                  <SoftBtn
                    $tone="#c94daa"
                    type="button"
                    onClick={() => setPaymentRecordInstallment({} as InvoiceInstallment)}
                  >
                    <CreditCard size={15} strokeWidth={2.2} />
                    Record Custom Payment
                  </SoftBtn>
                </PermissionGate>
              </CardHeader>

              <TableWrap>
                <PaymentsTable $minWidth={760}>
                  <thead>
                    <tr>
                      <th><span>Seq</span></th>
                      <th><span>Label</span></th>
                      <th><span>%</span></th>
                      <th><span>Amount</span></th>
                      <th><span>Status</span></th>
                      <th><span>Due Date</span></th>
                      <th style={{ textAlign: "right" }}><span>Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.installments.map((inst) => {
                      const isPaid = inst.paid || inst.status === "paid";
                      return (
                        <tr key={inst.id}>
                          <td>{inst.sequence}</td>
                          <td style={{ fontWeight: 600 }}>{inst.label}</td>
                          <td>{inst.percent}%</td>
                          <td><AmountCell>₹{inst.amount}</AmountCell></td>
                          <td>
                            {isPaid ? (
                              <PaidBadge><CheckCircle size={12} /> Paid</PaidBadge>
                            ) : (
                              <PendingBadge><Clock size={12} /> Pending</PendingBadge>
                            )}
                          </td>
                          <td>{inst.due_date || "—"}</td>
                          <td style={{ textAlign: "right" }}>
                            {!isPaid && (
                              <InstallmentActions>
                                <PermissionGate anyPermission={["invoice.create", "invoice.update", "payments_management.create", "payments_management.update"]}>
                                  <SoftBtn $tone="#35b7c8" type="button" onClick={() => setPaymentLinkInstallment(inst)}>
                                    <Link2 size={14} />
                                    Get Link
                                  </SoftBtn>
                                </PermissionGate>
                                <PermissionGate anyPermission={["invoice.create", "invoice.update", "payments_management.create", "payments_management.update"]}>
                                  <SoftBtn $tone="#31a66a" type="button" onClick={() => setPaymentRecordInstallment(inst)}>
                                    <CheckCircle size={14} />
                                    Mark Paid
                                  </SoftBtn>
                                </PermissionGate>
                              </InstallmentActions>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {invoice.installments.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center", padding: 24, color: "rgba(23,23,23,0.45)" }}>
                          No installments configured.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </SectionCard>

            <SectionCard>
              <CardHeader>
                <CardIcon $tone="#e44862">
                  <ReceiptText size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Payment History</CardTitle>
                  <CardSubtitle>Recorded payments against this invoice</CardSubtitle>
                </div>
              </CardHeader>

              <TableWrap>
                <PaymentsTable $minWidth={720}>
                  <thead>
                    <tr>
                      <th><span>ID</span></th>
                      <th><span>Date</span></th>
                      <th><span>Amount</span></th>
                      <th><span>Method</span></th>
                      <th><span>Reference</span></th>
                      <th><span>Notes</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p) => (
                      <tr key={p.id}>
                        <td style={{ color: "rgba(23,23,23,0.45)", fontWeight: 600 }}>#{p.id}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{p.payment_date}</td>
                        <td><AmountCell>₹{p.amount}</AmountCell></td>
                        <td>{PAYMENT_METHOD_LABELS[p.method]}</td>
                        <td>{p.reference || "—"}</td>
                        <td>{p.notes || "—"}</td>
                      </tr>
                    ))}
                    {invoice.payments.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "rgba(23,23,23,0.45)" }}>
                          No payments recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </SectionCard>
          </>
        )}
      </ScrollBody>

      {shareModalOpen && invoice && (
        <InvoiceShareModal
          invoice={invoice}
          onClose={() => setShareModalOpen(false)}
          onShareOfficial={handleOfficialShare}
          loading={sharingOfficial}
        />
      )}

      {paymentRecordInstallment !== null && (
        <PaymentRecordModal
          invoiceId={invoice.id}
          installment={paymentRecordInstallment.id ? paymentRecordInstallment : undefined}
          onClose={() => setPaymentRecordInstallment(null)}
          onSuccess={fetchDetails}
        />
      )}

      {paymentLinkInstallment !== null && (
        <PaymentLinkModal
          invoiceId={invoice.id}
          installment={paymentLinkInstallment}
          onClose={() => setPaymentLinkInstallment(null)}
          onSuccess={fetchDetails}
        />
      )}

      <DocumentPrintTemplate
        printRef={printRef}
        activeDoc={invoice}
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="invoice"
      />

      {deleteConfirm && (
        <DeletePopup
          text={`Are you sure you want to delete invoice ${invoice.invoice_no}?`}
          buttonText={deleting ? "Deleting..." : "Delete"}
          cancel={() => setDeleteConfirm(false)}
          Delete={handleConfirmDelete}
        />
      )}
    </PageContainer>
  );
};

export default InvoiceDetailPage;

// ─── Styled Components ────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -60px;
  right: -4%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.28) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 480px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  color: rgba(23, 23, 23, 0.72);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
    transform: translateY(-1px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  flex-shrink: 0;
  @media ${device.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatCard = styled.div<{ $tone: string }>`
  padding: 18px 20px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(p) => p.$tone};
    opacity: 0.85;
  }
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.48);
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const TabPill = styled.button<{ $active: boolean }>`
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%)"
      : "#fff"};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23, 23, 23, 0.62)")};
  padding: 10px 18px;
  border-radius: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 6px 16px rgba(201, 77, 170, 0.22)" : "none")};

  &:hover {
    ${(p) => !p.$active && "border-color: rgba(201, 77, 170, 0.25); color: #c94daa;"}
  }
`;

const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 4px;
`;

const SectionCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CardIcon = styled.div<{ $tone?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone || "#c94daa"};
  background: ${(p) => `${p.$tone || "#c94daa"}14`};
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const CardSubtitle = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionDivider = styled.div`
  width: 1px;
  height: 28px;
  background: rgba(23, 23, 23, 0.1);
  flex-shrink: 0;
  @media ${device.tablet} {
    display: none;
  }
`;

const ActionSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`;

const SoftBtn = styled.button<{ $tone: string; $solid?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$solid ? "transparent" : `${p.$tone}33`)};
  background: ${(p) => (p.$solid ? p.$tone : `${p.$tone}14`)};
  color: ${(p) => (p.$solid ? "#fff" : p.$tone)};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${(p) => (p.$solid ? `0 6px 16px ${p.$tone}40` : "none")};
  transition: all 0.15s ease;

  svg {
    flex-shrink: 0;
    display: block;
  }

  &:hover:not(:disabled) {
    background: ${(p) => (p.$solid ? p.$tone : `${p.$tone}22`)};
    border-color: ${(p) => (p.$solid ? "transparent" : `${p.$tone}55`)};
    transform: translateY(-1px);
    box-shadow: ${(p) =>
      p.$solid ? `0 8px 18px ${p.$tone}48` : `0 4px 12px ${p.$tone}18`};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const InfoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.48);
`;

const InfoValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #171717;
`;

const NotesBlock = styled.div`
  white-space: pre-wrap;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  line-height: 1.65;
  padding: 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const SummaryCard = styled.div`
  margin-left: auto;
  width: min(100%, 340px);
  padding: 20px 22px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.1) 0%, rgba(206, 86, 176, 0.08) 100%);
  border: 1px solid rgba(201, 77, 170, 0.14);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px dashed rgba(201, 77, 170, 0.25);
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #c94daa;
`;

const detailStatusColors: Record<string, { bg: string; color: string }> = {
  paid: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  fully_paid: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Active: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Completed: { bg: "rgba(53,183,200,0.12)", color: "#35b7c8" },
  partially_paid: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  sent: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  draft: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  Pending: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  not_paid: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  unpaid: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  overdue: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  Cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
  Shared: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  shared: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  "Remaining to share": { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  "remaining to share": { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => detailStatusColors[p.$status]?.bg ?? detailStatusColors[p.$status?.toLowerCase()]?.bg ?? "rgba(23,23,23,0.08)"};
  color: ${(p) => detailStatusColors[p.$status]?.color ?? detailStatusColors[p.$status?.toLowerCase()]?.color ?? "#171717"};
`;

const InlineBadge = styled(StatusBadge)``;

const PaidBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(49, 166, 106, 0.12);
  color: #31a66a;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const PendingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(240, 154, 62, 0.15);
  color: #f09a3e;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const InstallmentActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const SpinIcon = styled(Loader2)`
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
